// 3rd-party modules
import { Modal, Button, Input, message, Space } from "antd";
import { useEffect, useState } from "react";

// project modules
import Loader from "../shared/loader";
import useTranslation from "../../hooks/TranslationContext";
import { apiCall } from "../../helpers/apiHelper";

//apis
import * as CustomerApi from '../../apis/customerApi';

//Models
import { CustomerModel } from "../../models/customer";
import Table, { ColumnsType } from "antd/es/table";

// defines
type Props = {
  open: boolean;
  closeOnSave?: boolean;
  onSelect: (customer: CustomerModel) => void;
  onCancel: () => void;
};

export default function CustomerSearchModal({ open, closeOnSave = false, onSelect, onCancel }: Props) {
  const { t } = useTranslation();
  const [searchText, setSearchText] = useState("");
  const [customers, setCustomers] = useState<CustomerModel[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerModel | null>(null);
  const abortController = new AbortController();

  // Define columns for the table
  const columns: ColumnsType<CustomerModel> = [
    {
      title: "#",
      dataIndex: "customerId",
      key: "customerId",
    },
    {
      title: t("name"),
      dataIndex: "name",
      key: "name",
    },
    {
      title: t("mobile"),
      dataIndex: "mobile",
      key: "mobile",
    },
    {
      title: t("address"),
      dataIndex: "address",
      key: "address",
      render: (_, record) => (
        <span>
          {record.mahale && `${record.mahale}, `}
          {record.address}
        </span>
      ),
    },
    {
      key: "action",
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="primary"
            onClick={() => setSelectedCustomer(record)}
            disabled={selectedCustomer?.customerId === record.customerId}
          >
            {t("select")}
          </Button>
        </Space>
      ),
    },
  ];

  const onSearch = async () => {
    if (!searchText.trim()) {
      message.warning(t("please-enter-search-text"));
      return;
    }

    setLoading(true);
    try {
      const response = await apiCall(CustomerApi.getList(searchText, abortController.signal));

      if (response.success) {
        // Convert the response data to CustomerModel instances
        const customers = CustomerModel.toArrayOfClass(response.data?.value);
        setCustomers(customers);
      } else {
        message.error(response.error?.value || t("search-failed"));
      }
    } catch (error) {
      message.error(t("search-error"));
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = () => {
    if (!selectedCustomer) {
      message.warning(t("please-select-a-customer"));
      return;
    }
    onSelect(selectedCustomer);
    resetModal();
  };

  const resetModal = () => {
    setSearchText("");
    setCustomers([]);
    setSelectedCustomer(null);
  };

  useEffect(() => {
    return () => {
      abortController.abort();
    };
  }, []);

  return (
    <Modal
      title={t("search-customer")}
      open={open}
      onCancel={() => {
        resetModal();
        onCancel();
      }}
      footer={[
        <Button key="cancel" onClick={() => {
          resetModal();
          onCancel();
        }}>
          {t("cancel")}
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={onSubmit}
          disabled={!selectedCustomer}
        >
          {t("confirm")}
        </Button>,
      ]}
      width={800}
    >
      <div className="space-y-4">
        <div className="flex gap-2 mb-4">
          <Input
            placeholder={t("enter-customer-name-or-mobile")}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onPressEnter={onSearch}
            allowClear
          />
          <Button type="primary" onClick={onSearch}>
            {t("search")}
          </Button>
        </div>

        {loading && <Loader />}

        <Table
          columns={columns}
          dataSource={customers}
          rowKey="customerId"
          pagination={{ pageSize: 10 }}
          rowClassName={(record) =>
            selectedCustomer?.customerId === record.customerId ? "bg-blue-50" : ""
          }
          onRow={(record) => ({
            onClick: () => setSelectedCustomer(record),
          })}
          locale={{
            emptyText: searchText ? t("no-customers-found") : t("enter-search-to-find-customers"),
          }}
        />
      </div>
    </Modal>
  );
};
